import React, { useState, useRef } from 'react';
import { Upload, Loader2, ArrowLeft, Check, AlertTriangle, X, FileSpreadsheet, Download } from 'lucide-react';
import { bulkImportAPI } from '../../services/api';
import { toast } from 'sonner';

const ContactImportWizard = ({ importType = 'Buyer', onBack, onComplete }) => {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const label = importType === 'Buyer' ? 'Buyers (Customers)' : 'Suppliers (Vendors)';

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      setUploading(true);
      const data = await bulkImportAPI.previewContacts(file);
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse file');
    } finally { setUploading(false); }
  };

  const handleImport = async () => {
    if (!preview) return;
    const filtered = preview.items.filter(i => {
      if (importType === 'Buyer') return ['Buyer', 'Both'].includes(i.type) && !i.is_existing_customer;
      return ['Supplier', 'Both'].includes(i.type) && !i.is_existing_vendor;
    });
    if (filtered.length === 0) { toast.error('No new records to import'); return; }
    try {
      setImporting(true);
      setStep('importing');
      const res = await bulkImportAPI.confirmContacts({ items: filtered });
      setResult(res);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
      setStep('preview');
    } finally { setImporting(false); }
  };

  const relevantItems = preview?.items?.filter(i => {
    if (importType === 'Buyer') return ['Buyer', 'Both'].includes(i.type);
    return ['Supplier', 'Both'].includes(i.type);
  }) || [];

  const newItems = relevantItems.filter(i => importType === 'Buyer' ? !i.is_existing_customer : !i.is_existing_vendor);
  const existingItems = relevantItems.filter(i => importType === 'Buyer' ? i.is_existing_customer : i.is_existing_vendor);

  const downloadTemplate = () => {
    const headers = 'Company Name,Type,City,State,Pin/Zip,GSTIN,Company Email,Company Phone,Contact Person Name,Contact Person Email,Contact Person Phone,Address Line 1,Address Line 2,Country\n';
    const sampleRow = importType === 'Buyer'
      ? 'ABC Corp,Buyer,Mumbai,Maharashtra,400001,27AABCT1234F1Z5,info@abc.com,9876543210,John Doe,john@abc.com,9876543211,Plot 1 Industrial Area,,India\n'
      : 'XYZ Supplies,Supplier,Delhi,Delhi,110001,07AABCX5678G1Z9,info@xyz.com,9123456789,Jane Smith,jane@xyz.com,9123456780,123 Trade Centre,,India\n';
    const blob = new Blob([headers + sampleRow], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${importType.toLowerCase()}_import_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (step === 'done') {
    const imported = importType === 'Buyer' ? result?.customers_imported : result?.vendors_imported;
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="import-done">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 mb-2">Import Complete</h2>
        <p className="text-sm text-gray-600 mb-1">{imported || 0} {label} imported successfully</p>
        {result?.skipped > 0 && <p className="text-xs text-amber-600">{result.skipped} skipped (duplicates or empty)</p>}
        {result?.errors > 0 && <p className="text-xs text-red-600">{result.errors} errors encountered</p>}
        <button onClick={onComplete} className="mt-6 px-6 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
          Done
        </button>
      </div>
    );
  }

  if (step === 'importing') {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: '#24549c' }} />
        <h2 className="text-base font-bold text-gray-900 mb-1">Importing {label}...</h2>
        <p className="text-xs text-gray-500">Please wait while records are being created</p>
      </div>
    );
  }

  if (step === 'preview' && preview) {
    return (
      <div data-testid="import-preview">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => { setStep('upload'); setPreview(null); setError(''); }} className="p-1.5 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          </button>
          <div>
            <h2 className="text-base font-bold text-gray-900">Import Preview — {label}</h2>
            <p className="text-[11px] text-gray-500">{preview.total_rows} rows parsed from file</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-emerald-700">{newItems.length}</p>
            <p className="text-[10px] text-emerald-600 font-semibold">New (will import)</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-amber-700">{existingItems.length}</p>
            <p className="text-[10px] text-amber-600 font-semibold">Already Exist (skip)</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-gray-700">{relevantItems.length}</p>
            <p className="text-[10px] text-gray-600 font-semibold">Total {importType === 'Buyer' ? 'Buyers' : 'Suppliers'}</p>
          </div>
        </div>

        {preview.warnings_count > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {preview.warnings_count} warnings</p>
            <div className="mt-2 max-h-24 overflow-y-auto text-[10px] text-amber-600 space-y-0.5">
              {(preview.warnings || []).slice(0, 10).map((w, i) => <p key={i}>{w}</p>)}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-4">
          <div className="max-h-[400px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {['#', 'Name', 'City', 'State', 'GSTIN', 'Phone', 'Email', 'Status'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {relevantItems.map((item, idx) => {
                  const isExisting = importType === 'Buyer' ? item.is_existing_customer : item.is_existing_vendor;
                  return (
                    <tr key={idx} className={`border-t border-gray-50 ${isExisting ? 'opacity-50 bg-gray-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{item.company_name}</td>
                      <td className="px-3 py-2 text-gray-600">{item.city}</td>
                      <td className="px-3 py-2 text-gray-600">{item.state}</td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-[10px]">{item.gstin || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{item.contact_phone || item.company_phone || '-'}</td>
                      <td className="px-3 py-2 text-gray-600 truncate max-w-[120px]">{item.email || '-'}</td>
                      <td className="px-3 py-2">
                        {isExisting
                          ? <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">Exists</span>
                          : <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold">New</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={() => { setStep('upload'); setPreview(null); }} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancel
          </button>
          <button data-testid="confirm-import-btn" onClick={handleImport} disabled={newItems.length === 0}
            className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50"
            style={{ backgroundColor: '#24549c' }}>
            <Upload className="w-3.5 h-3.5" /> Import {newItems.length} {importType === 'Buyer' ? 'Buyers' : 'Suppliers'}
          </button>
        </div>
      </div>
    );
  }

  // UPLOAD step
  return (
    <div data-testid="import-upload">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div>
          <h2 className="text-base font-bold text-gray-900">Import {label}</h2>
          <p className="text-[11px] text-gray-500">Upload an Excel (.xlsx) or CSV file</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto">
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-all"
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: '#24549c' }} />
          ) : (
            <FileSpreadsheet className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          )}
          <p className="text-sm font-semibold text-gray-700">{uploading ? 'Parsing file...' : 'Click to upload Excel or CSV'}</p>
          <p className="text-[11px] text-gray-400 mt-1">Supported: .xlsx, .xlsm, .xls, .csv</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" onChange={handleFileSelect} className="hidden" />
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-2">
            <X className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-xs font-bold text-gray-700 mb-2">Expected Columns</h4>
          <div className="flex flex-wrap gap-1.5">
            {['Company Name', 'Type (Buyer/Supplier/Both)', 'City', 'State', 'PIN/ZIP', 'GSTIN', 'GSTIN Type', 'Company Email', 'Company Contact No', 'Contact Person', 'Address Name', 'Country', 'Party Branch', 'Sales Person Name'].map(col => (
              <span key={col} className="px-2 py-0.5 text-[10px] bg-white border border-gray-200 rounded-full text-gray-600">{col}</span>
            ))}
          </div>
          <button onClick={downloadTemplate} className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold hover:underline" style={{ color: '#24549c' }}>
            <Download className="w-3 h-3" /> Download Template CSV
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactImportWizard;
